import { Journal } from "@/types/journal";
import { I18nData } from "@/types/i18n";
import { Branding } from "@/types/branding";
import { useLocale } from "@/context/LocaleContext";
import { useSearch } from "@/context/SearchContext";
import MobileCollapsibleSection from "./mobile-collapsible-row";
import Icon from "@/components/common/Icon";
import { generatePaymentLink, generateLicenseLink, generatePublishingModelLink, generateJournalLink } from "@/utils/links";

interface Props {
  journal: Journal;
  i18nData: I18nData;
  branding: Branding | null;
}

export default function ResultCard({ journal, i18nData, branding }: Props) {
  const { locale } = useLocale();
  const { slug } = useSearch();
  const paymentIcon = journal.payment
    ? i18nData[locale]?.payments?.[journal.payment]
    : undefined;
  const licenseIcons = journal.licenses?.map(
    (license) => i18nData[locale]?.licenses?.[license]
  );

  const publishingModelIcon = journal.publishing_model
    ? i18nData[locale]?.publishing_models?.[journal.publishing_model]
    : undefined;

  const linkTarget = branding?.open_in_new_window ? "_blank" : "_self";

  return (
    <div id="resultCard">
      <a
        id="cardName"
        href={journal._id ? generateJournalLink(journal._id, slug) : '#'}
        target={journal._id ? linkTarget : undefined}
        className="accessibleLink"
      >
        {journal.name}
      </a>
      <div id="cardImprint">{journal.imprint}</div>
      <div id="iconWrapper"></div>
      <div className="mt-[10px]">
        {paymentIcon && (() => {
          // Only use universal link pattern, fallback to '#' if data is missing
          const paymentUrl = journal._id ? generatePaymentLink(journal._id, slug) : '#';
          
          return (
            <a
              href={paymentUrl}
              target={paymentUrl !== '#' ? linkTarget : undefined}
              className="accessibleLink"
            >
              <Icon
                icon={paymentIcon}
                width={24}
                height={24}
                alt="Payment option"
              />
            </a>
          );
        })()}
        <p className="mobileCardSubTooltip">{paymentIcon?.description?.trim()}</p>
      </div>
      {licenseIcons &&
        licenseIcons.length > 0 &&
        licenseIcons.map((license, index) => 
          license ? (
            <div key={license.description || index} className="mt-[10px]">
              {(() => {
                // Only use universal link pattern, fallback to '#' if data is missing
                const licenseKey = journal.licenses?.[index];
                const finalUrl = licenseKey && locale ? generateLicenseLink(licenseKey, locale, slug) : '#';
                
                return (
                  <a
                    href={finalUrl}
                    target={finalUrl !== '#' ? linkTarget : undefined}
                    className="accessibleLink"
                  >
                    <Icon
                      icon={license}
                      width={24}
                      height={24}
                      alt="License"
                    />
                  </a>
                );
              })()}
              <p className="mobileCardSubTooltip">{license.description?.trim()}</p>
            </div>
          ) : null
        )}
      <div className="mt-[10px]">
        {publishingModelIcon && (() => {
          // Only use universal link pattern, fallback to '#' if data is missing
          const finalUrl = journal.publishing_model && locale ? 
            generatePublishingModelLink(journal.publishing_model, locale, slug) : '#';
          
          return (
            <a
              href={finalUrl}
              target={finalUrl !== '#' ? linkTarget : undefined}
              className="accessibleLink"
            >
              <Icon
                icon={publishingModelIcon}
                width={24}
                height={24}
                alt="Publishing model"
              />
            </a>
          );
        })()}
        <p className="mobileCardTooltip">{journal.publishing_model}</p>
        <p className="mobileCardSubTooltip mt-[10px]">
          {publishingModelIcon?.description?.trim()}
        </p>
      </div>
      {journal.extra_info && journal.extra_info.trim().length > 0 && (
        <MobileCollapsibleSection
          collapsedTitle={i18nData.en?.show_notes || "Show notes"}
          expandedTitle={i18nData.en?.hide_notes || "Hide notes"}
        >
          <p>{journal.extra_info}</p>
        </MobileCollapsibleSection>
      )}
    </div>
  );
}
